import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { FormGroup, Input, InputGroup, InputGroupText, Label, PopoverBody } from 'reactstrap';
import { NumberParam, useQueryParam } from 'use-query-params';
import ControlledPopover from 'atom-components/popover';

type Props = {
  name: string;
  label: ReactNode;
  defaultValue: number;
  tooltip?: ReactNode;
  tooltipHeading?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
  symbol?: ReactNode;
};

export function useRangeQueryParam(
  name: string,
  defaultValue: number
): [number, ReturnType<typeof useQueryParam>[1]] {
  const [rangeParam, set] = useQueryParam(name, NumberParam);
  return useMemo(() => [rangeParam ?? defaultValue, set], [defaultValue, rangeParam, set]);
}

export default function RangeSlider(props: Props): ReactElement {
  const { name, label, defaultValue, tooltipHeading, tooltip, symbol, min, max, step } = props;

  const idPrefix = useId();
  const labelRef = useRef(null);
  const inputId = `${idPrefix}-input`;
  const tooltipId = `${idPrefix}-label`;

  // save as a query param for later access, this is debounced to reduce rerenders
  const [rangeParam, setRangeParam] = useRangeQueryParam(name, defaultValue);
  const onChangeDebounced = useRef(debounce((e) => setRangeParam(+e.target.value), 500)).current;
  // store an accurate value that always reflects the actual input
  const [actualValue, setActualValue] = useState(rangeParam);

  useEffect(() => () => onChangeDebounced.cancel(), [onChangeDebounced]);
  useEffect(() => setActualValue(rangeParam), [rangeParam]);

  return (
    <FormGroup className="flex-grow-1 flex-xl-grow-0 mb-3">
      <div className="d-flex">
        <Label className="fw-bold flex-grow-1 my-0" htmlFor={inputId}>
          {!tooltip && label}
          {tooltip && (
            <>
              <span ref={labelRef} tabIndex={0} role="button" aria-describedby={tooltipId}>
                {label} <FontAwesomeIcon icon={faCircleInfo} fixedWidth className="ms-1" />
              </span>

              <ControlledPopover target={labelRef} trigger="hover focus" title={tooltipHeading}>
                <PopoverBody>{tooltip}</PopoverBody>
              </ControlledPopover>
            </>
          )}
        </Label>

        <InputGroup className="input-group-sm ms-2 w-auto">
          {symbol && <InputGroupText className="fw-bold">{symbol}</InputGroupText>}
          <Input
            type="number"
            min={min}
            max={max}
            step={step}
            value={actualValue}
            onChange={(e): void => {
              setActualValue(+e.currentTarget.value);
              onChangeDebounced(e);
            }}
          />
        </InputGroup>
      </div>
      <input
        id={inputId}
        value={actualValue}
        type="range"
        className="form-range"
        onChange={(e): void => {
          setActualValue(+e.currentTarget.value);
          onChangeDebounced(e);
        }}
        min={min}
        max={max}
        step={step}
      />
    </FormGroup>
  );
}
